<template>
	<div
		style="
			background: #efefef;
			border: dashed;
			margin: 20px;
			width: calc(100% - 40px);
			height: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
		"
		@drop.prevent="filesDropped"
		@dragover.prevent
	>
		<div class="display-2" style="text-align: center">
			<v-container grid-list-xl text-xs-center>
				<v-layout row wrap>
					<v-flex xs12>
						<ContentTypeIcon height="200px" :type="type" />
						<p style="font-size: 32px">{{ type }}</p>
						<p>Drop XLSX file here</p>
						<p style="font-size: 32px">
							or
							<a @click="selectFiles">select from your computer</a>
						</p>
					</v-flex>
					<v-flex xs6 offset-xs3>
						<bulk-upload-download-templates :type="type" />
					</v-flex>
					<v-flex xs6 offset-xs3>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" depressed small flat class="cursor-default">
									<v-icon left>mdi-information-outline</v-icon>Advanced information
								</v-btn>
							</template>
							<span>
								Use PLANT URLs for the following fields: <br />
								- objective: "plant:/custom-id/OBJECTIVE_CUSTOM_ID"
							</span>
						</v-tooltip>
					</v-flex>
				</v-layout>
			</v-container>
		</div>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { ContentTypeIcon } from 'plant-common';
import { selectFileFromComputer } from '@/utils/bulkUpload';
import ptApiClient from 'plant-api-client';

import { STORE_NOTIFICATIONS_NAME, ACTION_NOTIFICATIONS_ERROR } from '@/store_constants/notifications';
import { STORE_LIBRARY_NAME, STATE_PLANT_ID } from '@/store_constants/library';

import BulkUploadDownloadTemplates from './DownloadTemplates.vue';
import { getContentId, getMajorVersion, getMinorVersion } from '../../../../../utils/dbTool';

import { ContentOperationStatus } from 'plant-common/src/models/contentOperationStatus';
import { getContentBodyConstructorFrom } from '@/classes/content/contentBodyFunctions';
import { APP_XLSX_MIME_TYPE, getSpreadsheetData } from '@/utils/spreadSheetFunctions';

/**
 * Build absolute PLANT url from custom_id (assuming the library_id is always the current one)
 */
async function buildResultObject(customId, library_id) {
	let result = {};
	try {
		const content = await ptApiClient.getContentByCustomId(customId);

		result = {
			$origin: `plant://${library_id}/content/${getContentId(content)}/${getMajorVersion(content)}/${getMinorVersion(
				content
			)}`,
			custom_id: customId
		};
	} catch (e) {
		// Manage when the url is malformed. Throw an error maybe?
		result = undefined;
	}

	return result;
}

/**
 * Get Custom ID from PLANT url if match regex or assume it is directly the custom_id
 */
function getCustomIdFromPlantUrl(plantUrl) {
	const match = /^plant:\/custom-id\/(.*)$/g.exec(plantUrl);

	if (match && match[1]) return match[1];

	return plantUrl.trim();
}

export default {
	name: 'BulkUploadUploadXls',
	props: ['type'],
	components: {
		ContentTypeIcon,
		BulkUploadDownloadTemplates
	},
	data() {
		return {
			allowed_types: [],
			new_contents: []
		};
	},
	computed: {
		...mapState(STORE_LIBRARY_NAME, {
			library_id: STATE_PLANT_ID
		})
	},
	methods: {
		...mapActions(STORE_NOTIFICATIONS_NAME, {
			error: ACTION_NOTIFICATIONS_ERROR
		}),
		async selectFiles() {
			const [file] = await selectFileFromComputer(APP_XLSX_MIME_TYPE, false);
			this.uploadXlsxTemplate(file);
		},
		filesDropped(evt) {
			if (evt.dataTransfer.files.length !== 1) {
				alert('You can only drop one file');
				return;
			}
			const file = evt.dataTransfer.files[0];
			if (file.type !== APP_XLSX_MIME_TYPE) {
				alert('You can only drop a XLSX file.');
				return;
			}
			this.uploadXlsxTemplate(file);
		},
		isValidImportData(importData) {
			if (importData.length > 0) return true;

			this.showAlert(`Nothing to import.`);
			return false;
		},
		isValidImportConfig(importConfig) {
			if (importConfig && importConfig.type) return true;

			this.showAlert(
				`Malformed File: IMPORT-CONFIG not defined correctly. Please, download specific template and try again.`
			);
			return false;
		},
		async resolvePlantUrlReferences(importedContent, cache) {
			if (!(importedContent.hasOwnProperty('objective') && importedContent.objective)) {
				return;
			}
			const objectiveCustomId = getCustomIdFromPlantUrl(importedContent.objective);
			if (!(objectiveCustomId in cache)) {
				cache[objectiveCustomId] = await buildResultObject(objectiveCustomId, this.library_id);
			}
			importedContent.objective = cache[objectiveCustomId];
		},
		buildContentFrom(importedContent) {
			const { custom_id, title, custom_revision } = importedContent;
			const ContentBodyConstructor = getContentBodyConstructorFrom(this.type);
			const body = new ContentBodyConstructor(importedContent);

			return {
				custom_id: custom_id + '',
				title: title + '',
				body,
				valid: true,
				custom_revision: custom_revision ? custom_revision + '' : '',
				status: ContentOperationStatus.notStarted
			};
		},
		async buildContentsFromImportData(import_data) {
			const cache = {};
			const contents = [];

			for await (const importedContent of import_data) {
				await this.resolvePlantUrlReferences(importedContent, cache);
				contents.push(this.buildContentFrom(importedContent));
			}

			return contents;
		},
		async uploadXlsxTemplate(file) {
			const { import_data, import_config } = await getSpreadsheetData(file);

			if (!this.isValidImportData(import_data)) return;
			if (!this.isValidImportConfig(import_config)) return;

			this.allowed_types = [import_config.type];

			this.new_contents = await this.buildContentsFromImportData(import_data);
			this.$emit('upload-finished', this.new_contents);
		},

		showAlert(message) {
			this.error({ message, timeout: -1 });
		}
	}
};
</script>

<style>
.type-name {
	font-size: 32px;
}

.cursor-default {
	cursor: default;
}
</style>
